import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";

import "./PublicFooter.css";

import { useNavigate } from "react-router-dom";
import { LayoutContainerModel } from "../../../../models/containers/layout/LayoutContainerModel";
import { LayoutsService } from "../../../../services/layout/layouts.service";
import { AssetsService } from "../../../../services/data/assets.service";
import { globalUriLoader } from "../../../../services/singletons/generic/global.uri.loader";
import { filterOptimizeMemory } from "../../../../hooks/utils/CommonUtils";
import { useIsVisible } from "../../../../hooks/pageUtils/UseIsVisible";
import { useSetFooterVisible } from "../../../../GlobalStateProvider";

const BasicCard = lazy(() =>
    import("../../../cards/generic/BasicCard")
        .then(module => ({ default: module.BasicCard }))
);

const BasicLogoCard = lazy(() =>
    import("../../../cards/generic/BasicLogoCard")
        .then(module => ({ default: module.BasicLogoCard }))
);

const RAVerifiedSeal = lazy(async () => {
    const module = await import("../../../buttons/marketing/social/reclame-aqui/RAVerifiedSeal");
    const component = (module as Record<string, unknown>).RAVerifiedSeal ?? (module as Record<string, unknown>).default;

    return { default: component as React.ComponentType<any> };
});


export default function PublicFooter () {
    const initialized = useRef(false);
    const setFooterIsVisible = useSetFooterVisible();

    const parentContainerRef = useRef<HTMLDivElement>(null);
    const footerVisible = useIsVisible(parentContainerRef.current);
    
    const layoutsService = useRef(new LayoutsService());
    const assetsService = useRef(new AssetsService());

    const [contactModels, setContactModels] = useState<Array<LayoutContainerModel>>([]);
    const [linkModels, setLinkModels] = useState<Array<LayoutContainerModel>>([]);

    const deliveryUri = useRef<string>("https://deliverydireto.com.br/diblasipizzas");
    const ifoodUri = useRef<string>("https://www.ifood.com.br/busca?q=di%20blasi");
    
    const [logoDeliveryDiretoSrc, setLogoDeliveryDiretoSrc] = useState<string | undefined>(undefined);
    const [logoIfoodSrc, setLogoIfoodSrcSrc] = useState<string | undefined>(undefined);
    const [logo99Src, setLogo99SrcSrc] = useState<string | undefined>(undefined);
    const [logoDiblasiPrimarySrc, setLogoDiblasiPrimarySrc] = useState<string | undefined>(undefined);

    const [logoTikTokSrc, setlogoTikTokSrc] = useState<string | undefined>(undefined);
    const [logoFacebookSrc, setlogoFacebookSrc] = useState<string | undefined>(undefined);
    const [logoInstagramSrc, setlogoInstagramSrc] = useState<string | undefined>(undefined);
    const [logoLinkedInSrc, setlogoLinkedInSrc] = useState<string | undefined>(undefined);
    const [logoTwitterSrc, setlogoTwitterSrc] = useState<string | undefined>(undefined);
    const [logoYouTubeSrc, setlogoYouTubeSrc] = useState<string | undefined>(undefined);

    const [textoRodape, setTextoRodape] = useState<string>("");
    const [knowCardModel, setKnowCardModel] = useState<LayoutContainerModel | undefined>(undefined);

    const navigate = useNavigate();

    useEffect(() => {
        setFooterIsVisible(footerVisible);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [footerVisible])

    useEffect(() => {        
        if (!initialized.current) {
            initialized.current = true;
            
            const fetchLayoutAssets = async () => {
                setLogoDiblasiPrimarySrc(await globalUriLoader.getDependentAssetUri("layout-logo-di-blasi-principal"));
            };

            const fetchContainers = async () => {
                let containers: LayoutContainerModel[] | undefined =
                    await layoutsService.current.handleListLayoutContainersWithRange(
                        1,
                        100,
                        undefined,
                        undefined,
                        "layout",
                        5,
                        true
                );

                if (containers !== undefined) {
                    let toDeleteIds: number[] = [];

                    for (const container of containers) {
                        let i: number = 0;

                        if (container.position < 4 && container.tag.startsWith("layout-rodape-contato")) {
                            i++;
                            container.content = assetsService.current.getDependentAssetUri(container.content);
                            setContactModels(prev => [...prev, container]);
                            toDeleteIds.push(container.id!);

                            if (i === 2) {
                                break;
                            }
                        }
                    }

                    containers = filterOptimizeMemory(containers, toDeleteIds);

                    for (const container of containers) {
                        let i: number = 0;

                        if (container.position < 5 && container.tag.startsWith("layout-link")) {
                            i++;
                            container.content = assetsService.current.getDependentAssetUri(container.content);
                            setLinkModels(prev => [...prev, container]);
                            toDeleteIds.push(container.id!);

                            if (i === 3) {
                                break;
                            }
                        }
                    }

                    containers = filterOptimizeMemory(containers, toDeleteIds);

                    for (const container of containers) {
                        if (container.position === 1 && container.tag.startsWith("layout-third-party-logo")) {
                            setLogoDeliveryDiretoSrc(assetsService.current.getDependentAssetUri(container.content));
                            toDeleteIds.push(container.id!);

                            break;
                        }
                    }

                    containers = filterOptimizeMemory(containers, toDeleteIds);

                    for (const container of containers) {
                        if (container.position === 2 && container.tag.startsWith("layout-third-party-logo")) {
                            setLogoIfoodSrcSrc(assetsService.current.getDependentAssetUri(container.content));
                            toDeleteIds.push(container.id!);

                            break;
                        }
                    }

                    containers = filterOptimizeMemory(containers, toDeleteIds);

                    for (const container of containers) {
                        if (container.position === 3 && container.tag.startsWith("layout-third-party-logo")) {
                            setLogo99SrcSrc(assetsService.current.getDependentAssetUri(container.content));
                            toDeleteIds.push(container.id!);

                            break;
                        }
                    }

                    containers = filterOptimizeMemory(containers, toDeleteIds);

                    for (const container of containers) {
                        if (container.position === 1 && container.tag.startsWith("layout-rodape-texto")) {
                            setTextoRodape(container.content);
                            toDeleteIds.push(container.id!);

                            break;
                        }
                    }

                    containers = filterOptimizeMemory(containers, toDeleteIds);

                    for (const container of containers) {
                        if (container.position === 1 && container.tag.startsWith("layout-rodape-social-logo")) {
                            setlogoTikTokSrc(assetsService.current.getDependentAssetUri(container.content));
                            toDeleteIds.push(container.id!);

                            break;
                        }
                    }

                    containers = filterOptimizeMemory(containers, toDeleteIds);

                    for (const container of containers) {
                        if (container.position === 2 && container.tag.startsWith("layout-rodape-social-logo")) {
                            setlogoFacebookSrc(assetsService.current.getDependentAssetUri(container.content));
                            toDeleteIds.push(container.id!);

                            break;
                        }
                    }

                    containers = filterOptimizeMemory(containers, toDeleteIds);

                    for (const container of containers) {
                        if (container.position === 3 && container.tag.startsWith("layout-rodape-social-logo")) {
                            setlogoInstagramSrc(assetsService.current.getDependentAssetUri(container.content));
                            toDeleteIds.push(container.id!);

                            break;
                        }
                    }

                    containers = filterOptimizeMemory(containers, toDeleteIds);

                    for (const container of containers) {
                        if (container.position === 4 && container.tag.startsWith("layout-rodape-social-logo")) {
                            setlogoLinkedInSrc(assetsService.current.getDependentAssetUri(container.content));
                            toDeleteIds.push(container.id!);

                            break;
                        }
                    }

                    containers = filterOptimizeMemory(containers, toDeleteIds);

                    for (const container of containers) {
                        if (container.position === 5 && container.tag.startsWith("layout-rodape-social-logo")) {
                            setlogoTwitterSrc(assetsService.current.getDependentAssetUri(container.content));
                            toDeleteIds.push(container.id!);

                            break;
                        }
                    }

                    containers = filterOptimizeMemory(containers, toDeleteIds);

                    for (const container of containers) {
                        if (container.position === 6 && container.tag.startsWith("layout-rodape-social-logo")) {
                            setlogoYouTubeSrc(assetsService.current.getDependentAssetUri(container.content));
                            toDeleteIds.push(container.id!);

                            break;
                        }
                    }

                    containers = filterOptimizeMemory(containers, toDeleteIds);

                    for (const container of containers) {
                        if (container.position === 1 && container.tag.startsWith("layout-rodape-know-card")) {
                            container.content = assetsService.current.getDependentAssetUri(container.content);
                            setKnowCardModel(container);
                            break;
                        }
                    }
                    
                    containers.length = 0;
                }
            };

            fetchContainers();
            fetchLayoutAssets();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    
    return (
        <Container fluid
            ref={parentContainerRef}
            style={{
                backgroundColor: "#1d1d1d", 
                color: "white"
            }}
        >
            <div className="large-vertical-combo-spacer"></div>
            <Row>
                <Col>
                    <table className="footer-left-align-table">
                        <thead></thead>
                        <tbody>
                            <tr className="table-primary">
                                <td>
                                    <img src={contactModels[0]?.content} alt="Atendimento" width="50px" 
                                        style={{minWidth: "50px"}}></img>
                                </td>
                                <td>
                                    <h6 className="text-light">{contactModels[0]?.name}</h6>
                                    <div className="text-light">{contactModels[0]?.initialContent}</div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </Col>
                <Col>
                    <table className="footer-left-align-table">
                        <thead></thead>
                        <tbody>
                            <tr className="table-primary">
                                <td>
                                    <img src={contactModels[1]?.content} alt="Atendimento" width="50px"
                                        style={{minWidth: "50px"}}></img>
                                </td>
                                <td>
                                    <h6 className="text-light">{contactModels[1]?.name}</h6>
                                    <div className="text-light">{contactModels[1]?.initialContent}</div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </Col>
                <Col>
                    <table className="footer-left-align-table">
                        <thead></thead>
                        <tbody>
                            <tr className="table-primary">
                                <td>
                                    <img src={contactModels[2]?.content} alt="Atendimento" width="50px"
                                        style={{minWidth: "50px"}}></img>
                                </td>
                                <td>
                                    <h6 className="text-light">{contactModels[2]?.name}</h6>
                                    <div className="text-light">{contactModels[2]?.initialContent}</div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </Col>
            </Row>
            <div className="large-vertical-combo-spacer"></div>
            <Row>
                <Col>
                    <div style={{width: "100%", display: "flex", justifyContent: "center"}}>
                        <Suspense fallback={null}>
                            <BasicLogoCard
                                width="128px"
                                height="128px"
                                model = {logoDeliveryDiretoSrc ? {
                                    imageSrc: logoDeliveryDiretoSrc,
                                    backgroundColor: "none",
                                    buttonClick: () => {window.open(deliveryUri.current, "_blank", "noreferrer");}
                                } : undefined}
                            />
                        </Suspense>
                    </div>
                </Col>
                <Col>
                    <div style={{width: "100%", display: "flex", justifyContent: "center"}}>
                        <Suspense fallback={null}>
                            <BasicLogoCard
                                width="128px"
                                height="128px"
                                model = {logoIfoodSrc ? {
                                    imageSrc: logoIfoodSrc,
                                    backgroundColor: "none",
                                    buttonClick: () => {window.open(ifoodUri.current, "_blank", "noreferrer");}
                                } : undefined}
                            />
                        </Suspense>
                    </div>
                </Col>
                <Col>
                    <div style={{width: "100%", display: "flex", justifyContent: "center"}}>
                        <Suspense fallback={null}>
                            <BasicLogoCard
                                width="128px"
                                height="128px"
                                model = {logo99Src ? {
                                    imageSrc: logo99Src,
                                    backgroundColor: "none",
                                    buttonClick: () => {}
                                } : undefined}
                            />
                        </Suspense>
                    </div>
                </Col>
            </Row>
            <div className="large-vertical-combo-spacer"></div>
            <div className="large-vertical-combo-spacer"></div>
            <Row>
                <Col>
                    <div className="btn-group-vertical">
                        <h5 className="text-light">Links</h5>
                        <a 
                            className="text-secondary-emphasis"
                            onClick={(e) => {e.preventDefault(); navigate(linkModels[0]?.additionalContent);}}
                            href="/#"
                            style={{textWrap: "nowrap"}}
                        >
                            {linkModels[0]?.name}
                        </a>
                        <a 
                            className="text-secondary-emphasis" 
                            onClick={(e) => {e.preventDefault(); navigate(linkModels[1]?.additionalContent);}}
                            href="/#"
                        >
                            {linkModels[1]?.name}
                        </a>
                        <a 
                            className="text-secondary-emphasis" 
                            onClick={(e) => {e.preventDefault(); navigate(linkModels[2]?.additionalContent);}}
                            href="/#"
                        >
                            {linkModels[2]?.name}
                        </a>
                        <a 
                            className="text-secondary-emphasis" 
                            onClick={(e) => {e.preventDefault(); navigate(linkModels[3]?.additionalContent);}}
                            href="/#"
                        >
                            {linkModels[3]?.name}
                        </a>
                    </div>
                </Col>
                {
                    logoDiblasiPrimarySrc &&
                    <Col>
                        <img src={logoDiblasiPrimarySrc} alt="Logo" style={{maxWidth: "256px"}}></img>
                    </Col>
                }
                <Col>
                    <h5 className="text-light">Redes Sociais</h5>
                    <div style={{width: "100%", display: "flex", justifyContent: "center"}}>
                        <table className="rounded-table" style={{width: "50%", minWidth: "290px"}}>
                            <thead></thead>
                            <tbody>
                                <tr className="table-primary">
                                    <td><a 
                                            href="https://www.tiktok.com/@diblasipizzas"
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                        >
                                            <img src={logoTikTokSrc} alt="TikTik" width="30px"></img>
                                        </a>
                                    </td>
                                    <td>
                                        <a 
                                            href="https://www.facebook.com/DiBlasiPizzas/"
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                        >
                                            <img src={logoFacebookSrc} alt="Facebook" width="30px"></img>
                                        </a>
                                    </td>
                                    <td>
                                        <a 
                                            href="https://www.instagram.com/diblasipizzas/"
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                        >
                                            <img src={logoInstagramSrc} alt="Instagram" width="30px"></img>
                                        </a>
                                    </td>
                                </tr>
                                <tr className="table-primary">
                                    <td>
                                        <a 
                                            href="http://br.linkedin.com/company/di-blasi-pizzas"
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                        >
                                            <img src={logoLinkedInSrc} alt="LinkedIn" width="30px"></img>
                                        </a>
                                    </td>
                                    <td>
                                        <a 
                                            href="https://x.com/diblasipizzas"
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                        >
                                            <img src={logoTwitterSrc} alt="Twitter" width="30px"></img>
                                        </a>
                                    </td>
                                    <td>
                                        <a 
                                            href="https://www.youtube.com/@DiBlasiPizzas"
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                        >
                                            <img src={logoYouTubeSrc} alt="YouTube" width="30px"></img>
                                        </a>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </Col>
            </Row>
            <div className="large-vertical-combo-spacer"></div>
            <Row>
                <Col></Col>
                <Col>
                    <div style={{width: "100%", display: "flex", justifyContent: "center"}}>
                        <Suspense fallback={null}>
                            <BasicCard
                                model = {knowCardModel ? {
                                    title: knowCardModel.name,
                                    text: knowCardModel.initialContent,
                                    imageSrc: knowCardModel.content,
                                    buttonText: "Saiba mais",
                                    buttonClick: () => navigate(knowCardModel.additionalContent)
                                } : undefined}
                            />
                        </Suspense>
                    </div>
                </Col>
                <Col></Col>
            </Row>
            <div className="large-vertical-combo-spacer"></div>
            <Row>
                <Col>
                    <div 
                        dangerouslySetInnerHTML={{ __html: textoRodape }} 
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <div className="btn-group-vertical">
                        <img src="https://diblasipizzas.com/api/assets/dependent/17F16983F8F0099B8A6B2B5180E654F0D4D97824D04C7B7FB5C1D253543033DD/content" alt="Nos avalie no google!" style={{marginInline: "auto", justifySelf: "center"}}/>
                        <div className="large-vertical-combo-spacer"></div>
                        <a 
                            type="button" 
                            href="https://www.google.com/search?q=di+blasi+pizzas&ie=UTF-8" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="btn btn-danger">
                                Deixe sua avaliação
                        </a>
                    </div>
                </Col>
            </Row>
            <div className="large-vertical-combo-spacer"></div>
            <Row>
                <Container style={{
                    width: "100%", 
                    display: "flex", 
                    flexWrap: "wrap", 
                    justifyContent: "center",
                    rowGap: "30px",
                }}>
                    <RAVerifiedSeal/>
                    <div style={{alignSelf: "center"}}>
                        <img src="https://diblasipizzas.com/api/assets/dependent/1E02FBFA17ED8631E3C952C8AE6CA1881089F4843D926B205DBFB562C877D899/content" alt="copyright"/>
                    </div>
                </Container>
            </Row>
            <div className="large-vertical-combo-spacer"></div>
            <Row>
                <Col>{new Date().getFullYear()} © DI BLASI FRANCHISING LTDA | CNPJ: 41.720.736/0001-11</Col>
            </Row>
            <div className="large-vertical-combo-spacer"></div>
        </Container>
    );
}